import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  const fullPath = normalizedPath.startsWith('uploads/')
    ? normalizedPath
    : `uploads/${normalizedPath}`;
  return `${backendBase}/${fullPath}`;
};

const DoctorList = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const res = await getRequest('doctors?limit=500', getAuthHeaders());
      if (res?.data?.success) {
        setDoctors(res.data.data || []);
      } else {
        toast.error(res?.data?.message || 'Failed to load doctors');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching doctors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [departmentFilter]);

  const filteredDoctors = departmentFilter
    ? doctors.filter((d) => d.department?.heading === departmentFilter)
    : doctors;

  const totalPages = Math.max(1, Math.ceil(filteredDoctors.length / pageSize));
  const pageDoctors = filteredDoctors.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await patchRequest(
        `doctors/${item.id}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      if (res?.data?.success) {
        toast.success(`Doctor ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchDoctors();
      } else {
        toast.error(res?.data?.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
    try {
      const res = await deleteRequest(`doctors/${item.id}`, getAuthHeaders());
      if (res?.data?.success) {
        toast.success('Doctor deleted successfully');
        fetchDoctors();
      } else {
        toast.error(res?.data?.message || 'Delete failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting doctor');
    }
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: 'Image',
      accessor: 'image',
      Cell: ({ row }) => {
        const image = row.original.image;
        if (!image) return <span className="text-muted">—</span>;
        return (
          <img
            src={getImageUrl(image)}
            alt={row.original.name}
            width={50}
            height={50}
            style={{ borderRadius: '4px', objectFit: 'cover' }}
          />
        );
      },
    },
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Department',
      accessor: 'department',
      Cell: ({ row }) =>
        row.original.department?.heading || (
          <span className="text-muted">—</span>
        ),
    },
    {
      Header: 'Education',
      accessor: 'education',
      Cell: ({ row }) =>
        row.original.education || <span className="text-muted">—</span>,
    },
    {
      Header: 'Number',
      accessor: 'number',
      Cell: ({ row }) =>
        row.original.number || <span className="text-muted">—</span>,
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <span className={`badge ${row.original.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-info mr-2"
            onClick={() => navigate(`/doctors/update/${row.original.id}`)}
          >
            Edit
          </button>
          <button
            className={`btn btn-sm mr-2 ${row.original.status === 'active' ? 'btn-warning' : 'btn-success'}`}
            onClick={() => handleToggleStatus(row.original)}
          >
            {row.original.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4 d-flex justify-content-between align-items-center">
        <h4 className="font-weight-bold">Doctors</h4>
        <button className="btn btn-success" onClick={() => navigate('/doctors/add')}>
          + Add Doctor
        </button>
      </div>

      <div className="mx-4 mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="col-md-4 col-sm-6 px-0">
            <select
              className="form-control"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All Departments</option>
              {[ ...new Set(
                  doctors.map((d) => d.department?.heading).filter(Boolean)
                ) ].map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center mt-5">Loading doctors...</div>
        ) : pageDoctors.length > 0 ? (
          <>
            <AdminTable tableHeaders={tableHeaders} tableData={pageDoctors} />
            <div className="d-flex justify-content-between align-items-center mt-4">
              <span className="text-muted">
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, filteredDoctors.length)} of{' '}
                {filteredDoctors.length} doctors
              </span>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-secondary mx-1"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="mx-2">Page {currentPage} of {totalPages}</span>
                <button
                  className="btn btn-secondary mx-1"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center mt-5 text-white">
            {departmentFilter ? 'No doctors in this department' : 'No doctors available'}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorList;
